import React, { useState } from 'react';
import {
  CheckIcon,
  PhoneIcon,
  CreditCardIcon,
  ChartBarIcon,
  CalendarIcon,
  CheckCircleIcon,
  Cog6ToothIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useSubscriptionPage } from './useSubscriptionPage';
import EnterpriseEnquiryModal from './EnterpriseEnquiryModal';

// Enterprise is a "Contact Sales" tier — no self-serve checkout. Its card
// shows volume-based pricing + these value props instead of price/credits.
const ENTERPRISE_FEATURES = [
  'Dedicated support personnel',
  'Custom CRM integrations (HubSpot, Salesforce, Zoho)',
];

const SubscriptionPage: React.FC = () => {
  const {
    usageStats,
    statsLoading,
    error,
    selectedPlan,
    processing,
    successMessage,
    expandedPlan,
    setExpandedPlan,
    creditPackages,
    creditPackagesLoading,
    subscriptionPlans,
    subscriptionPlansLoading,
    currency,
    getCurrencySymbol,
    handleSubscribe,
    handleAddCredits,
    handleManageSubscription,
    handleCancelPendingDowngrade,
    showConfirmModal,
    pendingPlanId,
    proceedWithSubscription,
    cancelSubscriptionChange,
  } = useSubscriptionPage();

  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);

  const getBorderColor = (planId: string) => {
    switch (planId) {
      case 'basic': return 'border-green-400';
      case 'growth': return 'border-amber-400';
      case 'pro': return 'border-cyan-400';
      case 'enterprise': return 'border-purple-400';
      default: return 'border-white/10';
    }
  };

  const currentPlanLabel = (plan: string) => {
    if (!plan) return 'Free';
    if (plan === 'free') return 'Free';
    const fromList = subscriptionPlans.find((p) => p.id === plan)?.name;
    if (fromList) return fromList;
    return plan.charAt(0).toUpperCase() + plan.slice(1);
  };

  const planTier = (planId: string) => ({ basic: 0, growth: 1, pro: 2, enterprise: 3 }[planId] ?? -1);
  const getPlanButtonLabel = (planId: string) => {
    const current = usageStats?.current_plan;
    if (usageStats?.pending_downgrade_plan === planId) return 'Downgrade Scheduled';
    if (current === planId) return 'Current Plan';
    if (!current || current === 'free') return 'Get plan';
    const currentTier = planTier(current);
    const targetTier = planTier(planId);
    if (targetTier > currentTier) return 'Upgrade';
    if (targetTier < currentTier) return 'Downgrade';
    return 'Get plan';
  };
  const isPlanDisabled = (planId: string) => {
    return usageStats?.current_plan === planId || usageStats?.pending_downgrade_plan === planId;
  };

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      <EnterpriseEnquiryModal open={showEnterpriseModal} onClose={() => setShowEnterpriseModal(false)} />
      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto custom-scrollbar">
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity"
              onClick={cancelSubscriptionChange}
            ></div>
            
            {/* Modal */}
            <div className="relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl shadow-2xl border border-slate-700 max-w-lg w-full p-5 sm:p-6 transform transition-all">
              {/* Close button */}
              <button
                onClick={cancelSubscriptionChange}
                className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>

              {/* Warning Icon */}
              <div className="flex justify-center mb-3">
                <div className="p-2.5 bg-yellow-500/10 rounded-full">
                  <ExclamationTriangleIcon className="h-10 w-10 text-yellow-500" />
                </div>
              </div>

              {/* Title */}
              <h3 className="text-xl sm:text-2xl font-bold text-white text-center mb-2">
                Change Subscription Plan?
              </h3>

              {/* Message */}
              <div className="space-y-2 mb-4">
                <p className="text-gray-300 text-center text-xs sm:text-sm">
                  You're on <span className="font-semibold text-cyan-400">{currentPlanLabel(usageStats?.current_plan || '')}</span>. Switching to <span className="font-semibold text-cyan-400">{subscriptionPlans.find(p => p.id === pendingPlanId)?.name || pendingPlanId}</span>.
                </p>

                {pendingPlanId && usageStats?.current_plan && planTier(pendingPlanId) > planTier(usageStats.current_plan) ? (
                  <>
                    {/* Upgrade: credits change now */}
                    <div className="bg-slate-700/30 border border-slate-600 rounded-lg p-3">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="text-gray-400 text-xs mb-0.5">Current Credits</div>
                          <div className="text-white font-bold text-base">{usageStats?.available_credits || 0}</div>
                        </div>
                        <div className="flex items-center justify-center px-2">
                          <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                          </svg>
                        </div>
                        <div className="flex-1 text-right">
                          <div className="text-gray-400 text-xs mb-0.5">New Credits (now)</div>
                          <div className="text-emerald-400 font-bold text-base">
                            {subscriptionPlans.find(p => p.id === pendingPlanId)?.credits || 0}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 space-y-1.5">
                      <p className="text-emerald-200 text-xs sm:text-sm font-semibold">Upgrade — takes effect immediately:</p>
                      <ul className="text-emerald-100 text-xs space-y-0.5 ml-1">
                        <li>• Your plan and credits update <strong>right now</strong>.</li>
                        <li>• You'll be charged a <strong>prorated amount</strong> for the rest of the current period, then the new plan price each month.</li>
                        <li>• Unused credits from your current plan won't carry over.</li>
                      </ul>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Downgrade: credits stay the same until next cycle */}
                    <div className="bg-slate-700/30 border border-slate-600 rounded-lg p-3">
                      <div className="flex flex-col gap-2.5">
                        <div className="flex items-center justify-between">
                          <div className="text-gray-400 text-xs">Credits now (no change)</div>
                          <div className="text-white font-bold text-base">{usageStats?.available_credits || 0}</div>
                        </div>
                        <div className="border-t border-slate-600/50"></div>
                        <div className="flex items-center justify-between">
                          <div className="text-gray-400 text-xs flex items-center gap-1.5">
                            <CalendarIcon className="h-3.5 w-3.5 text-yellow-400" />
                            Credits after next billing
                          </div>
                          <div className="text-yellow-400 font-bold text-base">
                            {subscriptionPlans.find(p => p.id === pendingPlanId)?.credits || 0}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 space-y-1.5">
                      <p className="text-yellow-200 text-xs sm:text-sm font-semibold">Downgrade — takes effect next billing cycle:</p>
                      <ul className="text-yellow-100 text-xs space-y-0.5 ml-1">
                        <li>• You <strong>keep your current plan and credits</strong> until your next billing date.</li>
                        <li>• At the start of the next cycle, your plan switches and credits reset to the new plan's amount.</li>
                        <li>• Your next invoice will be at the lower price. No proration charges.</li>
                        <li>• You can cancel this scheduled downgrade any time before it takes effect.</li>
                      </ul>
                    </div>
                  </>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={cancelSubscriptionChange}
                  className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors font-medium text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={() => pendingPlanId && proceedWithSubscription(pendingPlanId)}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white rounded-lg transition-all font-medium shadow-lg text-sm"
                >
                  Change plan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -right-32 sm:-right-64 -top-32 sm:-top-64 w-[400px] sm:w-[600px] lg:w-[800px] h-[400px] sm:h-[600px] lg:h-[800px] bg-gradient-to-r from-cyan-500/5 to-blue-500/5 rounded-full"></div>
        <div className="absolute -left-32 sm:-left-64 -bottom-32 sm:-bottom-64 w-[400px] sm:w-[600px] lg:w-[800px] h-[400px] sm:h-[600px] lg:h-[800px] bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-full"></div>
      </div>
      <div className="relative mx-auto px-2 sm:px-3 lg:px-8 py-2 sm:py-4 lg:py-6 xl:py-8">
        <div className="mb-3 sm:mb-6 lg:mb-8">
          <div className="flex items-center justify-between mb-1 sm:mb-2">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="p-1 sm:p-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg shadow-lg">
                <CreditCardIcon className="h-3 w-3 sm:h-5 sm:w-5 lg:h-6 lg:w-6 text-white" />
              </div>
              <h1 className="text-base sm:text-xl lg:text-3xl xl:text-4xl font-bold text-slate-50">
                Subscription & Billing
              </h1>
            </div>
            {usageStats?.current_plan && usageStats.current_plan !== 'free' && (
              <button
                onClick={handleManageSubscription}
                disabled={processing}
                className="px-3 sm:px-4 lg:px-6 py-1.5 sm:py-2 lg:py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 transition-all duration-300 flex flex-col items-center"
              >
                <div className="flex items-center whitespace-nowrap text-xs sm:text-sm lg:text-base">
                  <Cog6ToothIcon className="h-4 w-4 mr-1.5" />
                  {processing ? 'Loading...' : 'Manage Subscription'}
                </div>
                <span className="text-[10px] sm:text-xs text-cyan-100/70 mt-0.5">
                  (Cancel or view your subscription)
                </span>
              </button>
            )}
          </div>
          <p className="text-gray-300 text-xs sm:text-sm lg:text-lg">
            Manage your subscription plan and view usage statistics
          </p>
        </div>

        {error && (
          <div className="mb-6 bg-red-500/20 border border-red-400 text-red-100 px-4 py-3 rounded-lg">{error}</div>
        )}
        {successMessage && (
          <div className="mb-6 bg-green-500/20 border border-green-400 text-green-100 px-4 py-3 rounded-lg flex items-center">
            <CheckCircleIcon className="h-5 w-5 mr-2" />
            {successMessage}
          </div>
        )}
        {usageStats?.subscription_status === 'past_due' && (
          <div className="mb-6 bg-amber-500/20 border border-amber-400/50 text-amber-100 px-4 py-3 rounded-lg flex items-center gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-amber-400" />
            <span>Your payment is past due. Use <strong>Manage Subscription</strong> above to update your card and avoid service interruption.</span>
          </div>
        )}
        {usageStats?.pending_downgrade_plan && (
          <div className="mb-6 bg-blue-500/15 border border-blue-400/40 text-blue-100 px-4 py-3 rounded-lg flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 flex-shrink-0 text-blue-400" />
              <span className="text-sm">
                Downgrade to <strong className="text-blue-300">{subscriptionPlans.find(p => p.id === usageStats.pending_downgrade_plan)?.name || usageStats.pending_downgrade_plan}</strong> is scheduled for your next billing cycle. You keep your current plan and credits until then.
              </span>
            </div>
            <button
              onClick={handleCancelPendingDowngrade}
              disabled={processing}
              className="flex-shrink-0 px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-400/40 text-blue-200 rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
            >
              {processing ? '...' : 'Cancel Downgrade'}
            </button>
          </div>
        )}

        <div className="mb-2 sm:mb-4 lg:mb-6 grid grid-cols-3 gap-1 sm:gap-2 lg:gap-4">
          <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl rounded-lg sm:rounded-xl shadow-2xl border border-slate-700/50 p-1.5 sm:p-3 lg:p-6">
            <div className="flex items-center">
              <PhoneIcon className="h-3 w-3 sm:h-5 sm:w-5 lg:h-6 lg:w-6 text-cyan-400" />
              <div className="ml-1.5 sm:ml-3 lg:ml-4 min-w-0 flex-1">
                <p className="text-xs font-medium text-gray-300 truncate">Used</p>
                {statsLoading ? (
                  <div className="animate-pulse"><div className="h-3 sm:h-6 lg:h-8 w-6 sm:w-12 lg:w-16 bg-gray-600 rounded"></div></div>
                ) : (
                  <p className="text-xs sm:text-lg lg:text-2xl font-bold text-white">{usageStats?.total_consumed || 0}</p>
                )}
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl rounded-lg sm:rounded-xl shadow-2xl border border-slate-700/50 p-1.5 sm:p-3 lg:p-6">
            <div className="flex items-center">
              <ChartBarIcon className="h-3 w-3 sm:h-5 sm:w-5 lg:h-6 lg:w-6 text-green-400" />
              <div className="ml-1.5 sm:ml-3 lg:ml-4 min-w-0 flex-1">
                <p className="text-xs font-medium text-gray-300 truncate">Available</p>
                {statsLoading ? (
                  <div className="animate-pulse"><div className="h-3 sm:h-6 lg:h-8 w-6 sm:w-12 lg:w-16 bg-gray-600 rounded"></div></div>
                ) : (
                  <p className="text-xs sm:text-lg lg:text-2xl font-bold text-white">{usageStats?.available_credits || 0}</p>
                )}
              </div>
            </div>
          </div>
          <div className="group relative overflow-hidden bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl rounded-lg sm:rounded-xl shadow-2xl hover:shadow-blue-500/20 transition-all duration-300 transform hover:-translate-y-1 border border-slate-700/50 p-1.5 sm:p-3 lg:p-6">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-cyan-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative flex items-center">
              <div className="p-0.5 sm:p-2 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-lg shadow-lg">
                <CreditCardIcon className="h-2.5 w-2.5 sm:h-4 sm:w-4 lg:h-5 lg:w-5 text-white" />
              </div>
              <div className="ml-1 sm:ml-3 lg:ml-4 min-w-0 flex-1">
                <p className="text-xs font-medium text-slate-300 uppercase tracking-wider">Current Plan</p>
                {statsLoading ? (
                  <div className="animate-pulse"><div className="h-3 sm:h-5 lg:h-6 w-8 sm:w-16 lg:w-20 bg-slate-600 rounded"></div></div>
                ) : (
                  <div>
                    <p className="text-xs sm:text-sm lg:text-lg font-bold text-white capitalize">
                      {currentPlanLabel(usageStats?.current_plan || 'free')}
                    </p>
                    {usageStats?.subscription_status === 'past_due' && (
                      <p className="text-[10px] sm:text-xs text-amber-400 mt-0.5">Payment past due</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mb-2 sm:mb-4 lg:mb-6">
          <h2 className="text-sm sm:text-lg lg:text-2xl font-bold text-white mb-2 sm:mb-4 lg:mb-6">Choose Your Plan</h2>
          {subscriptionPlansLoading ? (
            <div className="text-center text-gray-400 py-4">Loading subscription plans...</div>
          ) : subscriptionPlans.length === 0 ? (
            <div className="text-center text-gray-400 py-4">No subscription plans available</div>
          ) : (
            <>
              <div className="block sm:hidden space-y-2">
                {subscriptionPlans.map((plan) => {
                  const isCurrentPlan = usageStats?.current_plan === plan.id;
                  const isExpanded = expandedPlan === plan.id;
                  const isEnterprise = plan.id === 'enterprise';
                  const features = isEnterprise ? ENTERPRISE_FEATURES : plan.features;
                  return (
                    <div
                      key={plan.id}
                      className={`relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 active:scale-[0.99] border ${
                        isCurrentPlan
                          ? plan.id === 'basic' ? 'bg-gradient-to-br from-emerald-500/20 to-slate-800/90 border-emerald-400/50 card-glow-emerald' : plan.id === 'growth' ? 'bg-gradient-to-br from-amber-500/20 to-slate-800/90 border-amber-400/50 card-glow-amber' : plan.id === 'pro' ? 'bg-gradient-to-br from-cyan-500/20 to-slate-800/90 border-cyan-400/50 card-glow-cyan' : plan.id === 'enterprise' ? 'bg-gradient-to-br from-violet-500/20 to-slate-800/90 border-violet-400/50 card-glow-violet' : 'bg-gradient-to-b from-emerald-500/10 to-slate-800/90 border-emerald-400/60 card-glow-emerald'
                          : plan.id === 'basic' ? 'bg-gradient-to-br from-emerald-500/12 to-slate-800/80 border-slate-600/50' : plan.id === 'growth' ? 'bg-gradient-to-br from-amber-500/12 to-slate-800/80 border-slate-600/50' : plan.id === 'pro' ? 'bg-gradient-to-br from-cyan-500/12 to-slate-800/80 border-slate-600/50' : plan.id === 'enterprise' ? 'bg-gradient-to-br from-violet-500/12 to-slate-800/80 border-slate-600/50' : 'bg-slate-800/60 border border-slate-600/50'
                      } backdrop-blur-xl`}
                      onClick={() => setExpandedPlan(isExpanded ? null : plan.id)}
                    >
                      <div className={`h-1 w-full ${
                        plan.id === 'basic' ? 'bg-gradient-to-r from-emerald-400 via-green-400 to-teal-500' :
                        plan.id === 'growth' ? 'bg-gradient-to-r from-amber-400 via-orange-400 to-yellow-500' :
                        plan.id === 'pro' ? 'bg-gradient-to-r from-cyan-400 via-sky-400 to-blue-500' :
                        plan.id === 'enterprise' ? 'bg-gradient-to-r from-violet-400 via-purple-400 to-fuchsia-500' : 'bg-slate-500'
                      }`} />
                      {isCurrentPlan && (
                        <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent transform -skew-x-12 animate-shimmer"></div>
                        </div>
                      )}
                      {usageStats?.pending_downgrade_plan === plan.id && (
                        <span className="absolute top-2 right-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs font-bold px-2.5 py-1 rounded-md shadow-md flex items-center gap-1">
                          <CalendarIcon className="h-3 w-3" /> Scheduled
                        </span>
                      )}
                      {plan.popular && !isCurrentPlan && usageStats?.pending_downgrade_plan !== plan.id && (
                        <span className="absolute top-2 right-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-xs font-bold px-2.5 py-1 rounded-md shadow-md">Popular</span>
                      )}
                      {isCurrentPlan && usageStats?.pending_downgrade_plan !== plan.id && (
                        <span className="absolute top-2 right-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-xs font-bold px-2.5 py-1 rounded-md shadow-md flex items-center gap-1">
                          <CheckIcon className="h-3 w-3" /> Current
                        </span>
                      )}
                      <div className="relative p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-base font-bold text-white">{plan.name}</h3>
                            <p className="text-xs text-slate-400 mt-0.5">{isEnterprise ? 'Custom volume-based pricing' : `${plan.credits} credits`}</p>
                            <div className="mt-2 space-y-1">
                              {features.slice(0, 2).map((f, i) => (
                                <div key={i} className="flex items-center gap-2">
                                  <span className="w-4 h-4 rounded-full bg-emerald-500/25 flex items-center justify-center flex-shrink-0 ring-1 ring-emerald-400/20">
                                    <CheckIcon className="h-2.5 w-2.5 text-emerald-400" />
                                  </span>
                                  <span className="text-xs text-slate-300 truncate">{f}</span>
                                </div>
                              ))}
                              {!isExpanded && features.length > 2 && (
                                <span className="text-xs text-slate-500">+{features.length - 2} more</span>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-end ml-3">
                            {isEnterprise ? (
                              <>
                                <span className="text-sm font-bold text-violet-300 text-right">Custom pricing</span>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setShowEnterpriseModal(true); }}
                                  className="mt-2 px-3 py-2 rounded-xl text-xs font-semibold transition-all bg-gradient-to-r from-violet-500 to-purple-600 text-white whitespace-nowrap"
                                >
                                  Contact Sales
                                </button>
                              </>
                            ) : (
                              <>
                                <span className={`text-xl font-bold tabular-nums ${plan.id === 'basic' ? 'text-emerald-300' : plan.id === 'growth' ? 'text-amber-300' : plan.id === 'pro' ? 'text-cyan-300' : 'text-white'}`}>{getCurrencySymbol(currency)}{plan.price}</span>
                                <span className="text-xs text-slate-400">/mo</span>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleSubscribe(plan.id); }}
                                  disabled={processing || isPlanDisabled(plan.id)}
                                  className={`mt-2 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
                                    isPlanDisabled(plan.id) ? 'bg-slate-700/50 text-slate-400 cursor-not-allowed' : plan.id === 'basic' ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white' : plan.id === 'growth' ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white' : plan.id === 'pro' ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white' : 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white'
                                  }`}
                                >
                                  {processing && selectedPlan === plan.id ? '...' : getPlanButtonLabel(plan.id)}
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="mt-4 pt-4 border-t border-slate-700/50 space-y-2">
                            <h4 className="text-xs font-semibold text-white mb-2">All features</h4>
                            {features.map((feature, index) => (
                              <div key={index} className="flex items-start gap-2">
                                <span className="w-4 h-4 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <CheckIcon className="h-2.5 w-2.5 text-emerald-400" />
                                </span>
                                <span className="text-xs text-slate-300">{feature}</span>
                              </div>
                            ))}
                            <p className="text-xs text-slate-500 mt-2">Tap to collapse</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6 xl:gap-8 items-stretch">
                {subscriptionPlans.map((plan) => {
                  const isCurrentPlan = usageStats?.current_plan === plan.id;
                  const isBasic = plan.id === 'basic';
                  const isGrowth = plan.id === 'growth';
                  const isPro = plan.id === 'pro';
                  const isEnterprise = plan.id === 'enterprise';
                  const accentBar = isBasic ? 'from-emerald-400 via-green-400 to-teal-500' : isGrowth ? 'from-amber-400 via-orange-400 to-yellow-500' : isPro ? 'from-cyan-400 via-sky-400 to-blue-500' : isEnterprise ? 'from-violet-400 via-purple-400 to-fuchsia-500' : 'from-slate-400 to-slate-500';
                  const priceColor = isBasic ? 'text-emerald-300' : isGrowth ? 'text-amber-300' : isPro ? 'text-cyan-300' : isEnterprise ? 'text-violet-300' : 'text-white';
                  const glowHover = isBasic ? 'card-glow-hover-emerald' : isGrowth ? 'card-glow-hover-amber' : isPro ? 'card-glow-hover-cyan' : isEnterprise ? 'card-glow-hover-violet' : '';
                  const btnGradient = isBasic ? 'from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500' : isGrowth ? 'from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500' : isPro ? 'from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500' : isEnterprise ? 'from-violet-500 to-purple-600 hover:from-violet-400 hover:to-purple-500' : 'from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500';
                  const features = isEnterprise ? ENTERPRISE_FEATURES : plan.features;
                  return (
                    <div
                      key={plan.id}
                      className={`relative flex flex-col rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 backdrop-blur-xl border ${
                        isCurrentPlan
                          ? isBasic ? 'bg-gradient-to-br from-emerald-500/25 via-slate-800/90 to-slate-900 border-emerald-400/50 card-glow-emerald' : isGrowth ? 'bg-gradient-to-br from-amber-500/25 via-slate-800/90 to-slate-900 border-amber-400/50 card-glow-amber' : isPro ? 'bg-gradient-to-br from-cyan-500/25 via-slate-800/90 to-slate-900 border-cyan-400/50 card-glow-cyan' : isEnterprise ? 'bg-gradient-to-br from-violet-500/25 via-slate-800/90 to-slate-900 border-violet-400/50 card-glow-violet' : 'bg-slate-800/80 border-emerald-400/50 card-glow-emerald'
                          : plan.popular
                            ? 'bg-gradient-to-br from-cyan-500/20 via-slate-800/85 to-slate-900 border-cyan-400/40 ring-1 ring-cyan-400/30 card-glow-cyan ' + glowHover
                            : isBasic ? 'bg-gradient-to-br from-emerald-500/12 via-slate-800/80 to-slate-900 border-slate-600/50 hover:border-emerald-400/40 ' + glowHover : isGrowth ? 'bg-gradient-to-br from-amber-500/12 via-slate-800/80 to-slate-900 border-slate-600/50 hover:border-amber-400/40 ' + glowHover : isPro ? 'bg-gradient-to-br from-cyan-500/12 via-slate-800/80 to-slate-900 border-slate-600/50 hover:border-cyan-400/40 ' + glowHover : isEnterprise ? 'bg-gradient-to-br from-violet-500/12 via-slate-800/80 to-slate-900 border-slate-600/50 hover:border-violet-400/40 ' + glowHover : 'bg-slate-800/60 border-slate-600/50 hover:border-slate-500'
                      }`}
                    >
                      <div className={`h-1.5 w-full bg-gradient-to-r ${accentBar}`} />
                      {isCurrentPlan && (
                        <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent transform -skew-x-12 animate-shimmer"></div>
                        </div>
                      )}
                      {usageStats?.pending_downgrade_plan === plan.id && (
                        <div className="absolute top-4 right-4">
                          <span className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs font-bold px-3 py-1.5 rounded-md shadow-lg shadow-blue-500/30 flex items-center gap-1.5">
                            <CalendarIcon className="h-3.5 w-3.5" /> Scheduled
                          </span>
                        </div>
                      )}
                      {plan.popular && !isCurrentPlan && usageStats?.pending_downgrade_plan !== plan.id && (
                        <div className="absolute top-4 right-4">
                          <span className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-xs font-bold px-3 py-1.5 rounded-md shadow-lg shadow-cyan-500/30">Popular</span>
                        </div>
                      )}
                      {isCurrentPlan && usageStats?.pending_downgrade_plan !== plan.id && (
                        <div className="absolute top-4 right-4">
                          <span className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-xs font-bold px-3 py-1.5 rounded-md shadow-lg shadow-emerald-500/30 flex items-center gap-1.5">
                            <CheckIcon className="h-3.5 w-3.5" /> Current
                          </span>
                        </div>
                      )}
                      <div className="relative p-5 lg:p-6 xl:p-8 flex flex-col flex-1">
                        <div className="text-center mb-6">
                          <h3 className="text-xl lg:text-2xl font-bold text-white tracking-tight">{plan.name}</h3>
                          {isEnterprise ? (
                            <div className="mt-4">
                              <span className={`text-2xl lg:text-3xl font-extrabold ${priceColor}`}>Custom pricing</span>
                              <p className="mt-2 text-slate-400 text-sm font-medium">Volume-based — tailored to your usage</p>
                            </div>
                          ) : (
                            <>
                              <div className="mt-4 flex items-baseline justify-center gap-1">
                                <span className={`text-3xl lg:text-4xl font-extrabold tabular-nums ${priceColor}`}>{getCurrencySymbol(currency)}{plan.price}</span>
                                <span className="text-slate-400 text-sm font-medium">/mo</span>
                              </div>
                              <p className="mt-2 text-slate-400 text-sm font-medium">{plan.credits} credits included</p>
                            </>
                          )}
                        </div>
                        <ul className="space-y-3 flex-1">
                          {features.map((feature, index) => (
                            <li key={index} className="flex items-start gap-3">
                              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-500/25 flex items-center justify-center mt-0.5 ring-1 ring-emerald-400/20">
                                <CheckIcon className="h-3 w-3 text-emerald-400" />
                              </span>
                              <span className="text-slate-300 text-sm leading-snug">{feature}</span>
                            </li>
                          ))}
                        </ul>
                        <div className="mt-6 pt-6 border-t border-white/10 flex-shrink-0">
                          {isEnterprise ? (
                            <button
                              onClick={() => setShowEnterpriseModal(true)}
                              className={`w-full py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-200 bg-gradient-to-r ${btnGradient} text-white shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-violet-400`}
                            >
                              Contact Sales
                            </button>
                          ) : (
                            <button
                              onClick={() => handleSubscribe(plan.id)}
                              disabled={processing || isPlanDisabled(plan.id)}
                              className={`w-full py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-200 ${
                                isPlanDisabled(plan.id)
                                  ? 'bg-slate-700/50 text-slate-400 border border-slate-600 cursor-not-allowed'
                                  : `bg-gradient-to-r ${btnGradient} text-white shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-cyan-400`
                              }`}
                            >
                              {processing && selectedPlan === plan.id ? 'Processing...' : getPlanButtonLabel(plan.id)}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-600/50 p-4 sm:p-5 lg:p-6 shadow-xl">
          <h2 className="text-lg lg:text-2xl font-bold text-white mb-1">Add Extra Credits</h2>
          <p className="text-slate-400 text-sm mb-4 lg:mb-5">
            Need more minutes? Purchase additional credits that roll over to the next month.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
            {creditPackagesLoading ? (
              <div className="col-span-full text-center text-slate-400 py-8">Loading credit packages...</div>
            ) : creditPackages.length === 0 ? (
              <div className="col-span-full text-center text-slate-400 py-8">No credit packages available</div>
            ) : (
              creditPackages.map((option, idx) => {
                const isPopular = option.popular;
                const tint = isPopular ? 'from-cyan-500/25 via-slate-800/90 to-blue-900/80' : idx === 0 ? 'from-emerald-500/15 via-slate-800/85 to-slate-900' : idx === 2 ? 'from-violet-500/15 via-slate-800/85 to-slate-900' : 'from-slate-800/80 to-slate-900';
                const borderCls = isPopular ? 'border-cyan-400/50 ring-1 ring-cyan-400/20 card-glow-cyan card-glow-hover-cyan' : 'border-slate-600/50 hover:border-slate-500';
                const barGradient = isPopular ? 'from-cyan-400 via-sky-400 to-blue-500' : idx === 0 ? 'from-emerald-400 to-teal-500' : idx === 2 ? 'from-violet-400 to-purple-500' : 'from-slate-500 to-slate-400';
                const priceColor = isPopular ? 'text-cyan-300' : idx === 0 ? 'text-emerald-300' : idx === 2 ? 'text-violet-300' : 'text-white';
                return (
                  <div
                    key={option.packageId}
                    className={`group relative rounded-2xl border overflow-hidden transition-all duration-300 hover:-translate-y-1 bg-gradient-to-br ${tint} ${borderCls}`}
                  >
                    <div className={`h-1.5 w-full bg-gradient-to-r ${barGradient}`} />
                    {isPopular && (
                      <span className="absolute top-3 right-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-xs font-bold px-3 py-1.5 rounded-md shadow-lg shadow-cyan-500/30">Best value</span>
                    )}
                    <div className="p-5 lg:p-6 text-center">
                      <p className="text-slate-400 text-sm font-semibold uppercase tracking-wider">Credits</p>
                      <p className="text-2xl lg:text-3xl font-extrabold text-white mt-1 tabular-nums">{option.credits}</p>
                      <p className={`mt-3 text-2xl lg:text-3xl font-extrabold tabular-nums ${priceColor}`}>{getCurrencySymbol(currency)}{option.price}</p>
                      <p className="text-slate-500 text-xs mt-1">One-time purchase</p>
                      <button
                        onClick={() => handleAddCredits(option.packageId)}
                        disabled={processing}
                        className="mt-4 w-full py-3 px-4 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold text-sm hover:from-cyan-400 hover:to-blue-500 hover:shadow-lg hover:shadow-cyan-500/30 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-900 disabled:opacity-50 transition-all duration-200"
                      >
                        {processing ? 'Processing...' : 'Add credits'}
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {usageStats?.next_billing_date && (
          <div className="mt-2 sm:mt-4 lg:mt-6 bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl rounded-lg sm:rounded-xl shadow-2xl border border-slate-700/50 p-2 sm:p-4 lg:p-6">
            <div className="flex items-center">
              <CalendarIcon className="h-3 w-3 sm:h-5 sm:w-5 lg:h-6 lg:w-6 text-gray-300" />
              <div className="ml-1.5 sm:ml-3">
                <p className="text-xs font-medium text-gray-300">Next Billing Date</p>
                <p className="text-xs sm:text-base lg:text-lg font-semibold text-white">
                  {new Date(usageStats.next_billing_date).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPage;
